import { Page } from 'components/Page';
import { useSelector } from 'hooks/useSelector';
import * as React from 'react';
import { useDispatch } from 'react-redux';
import {
  NAVIGATE_MODAL_BACK,
  doReportingClosePage,
  doReportingGetReportListData,
  doReportingMergeDraftFilterValues,
  doReportingResetDraftFilterValues,
  doReportingSelectReport,
} from 'store/actions';
import {
  selectRptReportBasics,
  selectRptReportDraftFilterIsDirty,
  selectRptReportDraftFilterValues,
  selectRptReportFilterOptions,
  selectRptReportLoaded,
} from 'store/remote/selectors';
import { ReportEnum, ReportTypes } from 'types/Reports';
import { useEsc } from 'hooks/useEsc';
import { DateRangeField } from 'components/Field/DateRangeField';
import { Card } from 'components/Card';
import { TextAreaField } from 'components/Field/TextAreaField/TextAreaField';
import { OptionField, OptionFieldOption } from 'components/Field/OptionField';
import { noop, sortBy } from 'lodash';
import { dateStringToNumber, numberToDateString } from 'lib/riskbase';
import { ICONS } from 'components/Icon';
import { useConfirmUnsaved } from 'hooks/useConfirm';
import { smartText } from 'lib/str';
import { PageHeader } from 'components/PageHeader';

type Report = ReportTypes[ReportEnum.checkschedule];

export const Filter: React.FC<{
  reportKey: string;
}> = ({ reportKey }) => {
  const dispatch = useDispatch();
  const confirmUnsaved = useConfirmUnsaved();

  const basics = useSelector((state) =>
    selectRptReportBasics(state, reportKey),
  )!;

  const isLoaded = useSelector((state) =>
    selectRptReportLoaded(state, reportKey),
  )!;

  const filterOptions = useSelector((state) =>
    selectRptReportFilterOptions(state, reportKey),
  ) as Report['filterOptions'];

  const draftFilterValues = useSelector((state) =>
    selectRptReportDraftFilterValues(state, reportKey),
  ) as Report['filterValues'];

  const draftFilterIsDirty = useSelector((state) =>
    selectRptReportDraftFilterIsDirty(state, reportKey),
  );

  const checksOptions = React.useMemo(
    () =>
      (filterOptions.checks || [])
        .reduce<OptionFieldOption<string>[][]>(
          (acc, check) => {
            acc[check.deleted ? 1 : 0]!.push({
              name: check.name,
              internalValue: check.value,
              groupName: check.deleted ? 'Deleted Checks' : 'Current Checks',
            });
            return acc;
          },
          [[], []],
        )
        .filter((c) => !!c.length)
        .flat(),
    [filterOptions.checks],
  );

  const elementsOptions = React.useMemo(
    () =>
      (filterOptions.elements || [])
        .reduce<OptionFieldOption<string>[][]>(
          (acc, element) => {
            const index = element.checks
              ? !element.deleted
                ? 0
                : 2
              : !element.deleted
                ? 1
                : 3;
            acc[index]!.push({
              name: element.name,
              internalValue: element.value,
              groupName: element.deleted
                ? `Deleted Elements${!element.checks ? ' Without Checks' : ''}`
                : `Current Elements${!element.checks ? ' Without Checks' : ''}`,
            });
            return acc;
          },
          [[], [], []],
        )
        .filter((c) => !!c.length)
        .flat(),
    [filterOptions.elements],
  );

  const organisationOptions = React.useMemo(
    () =>
      sortBy(
        (filterOptions.organisations || []).map((organisation) => ({
          name: organisation.name,
          internalValue: organisation.value,
        })),
        ['name'],
      ),
    [filterOptions.organisations],
  );

  const showOutOf = React.useCallback(
    (total: number, chosen: number) =>
      chosen ? `${chosen} / ${total}` : total,
    [],
  );

  const handleSetFilterValues = React.useCallback(
    (values: Partial<Report['filterValues']>) =>
      dispatch(
        doReportingMergeDraftFilterValues({
          key: reportKey,
          filterValues: values,
          loadData: false,
        }),
      ),
    [dispatch, reportKey],
  );

  const handleSetFilterValue = React.useCallback(
    <
      T extends keyof Report['filterValues'],
      V extends Report['filterValues'][T],
    >(
      name: T,
      value: V,
    ) =>
      dispatch(
        doReportingMergeDraftFilterValues({
          key: reportKey,
          filterValues: { [name]: value },
          loadData: false,
        }),
      ),
    [dispatch, reportKey],
  );

  const handleSetOrganisationIds = React.useCallback(
    (newOrganisationIds: string[]) =>
      basics?.id
        ? dispatch(
            doReportingSelectReport({
              id: basics.id,
              key: reportKey,
              filter: {
                ...draftFilterValues,
                organisationIds: newOrganisationIds,
              },
              loadData: isLoaded,
            }),
          )
        : noop,
    [basics.id, dispatch, draftFilterValues, isLoaded, reportKey],
  );

  const handleApplyFilter = React.useCallback(
    () =>
      dispatch(
        doReportingGetReportListData({
          key: reportKey,
          nextPage: false,
          closeModals: true,
          overlayLoader: true,
        }),
      ),
    [dispatch, reportKey],
  );

  const handleClose = React.useCallback(() => {
    const doClose = () => {
      if (isLoaded) {
        dispatch(doReportingResetDraftFilterValues({ key: reportKey }));
      } else {
        dispatch(doReportingClosePage({ key: reportKey }));
      }
      dispatch(NAVIGATE_MODAL_BACK());
    };
    if (draftFilterIsDirty) {
      confirmUnsaved({
        onSave: handleApplyFilter,
        onDiscard: doClose,
        text: smartText("You haven't applied your changes"),
        onDiscardText: 'Discard Filter',
        onSaveText: 'Apply Filter',
      });
      return;
    }
    doClose();
  }, [
    confirmUnsaved,
    dispatch,
    draftFilterIsDirty,
    isLoaded,
    reportKey,
    handleApplyFilter,
  ]);

  useEsc(handleClose);

  return (
    <Page
      scrollView
      header={
        <PageHeader
          title={basics.name}
          onPressCancelButton={handleClose}
          onPressSaveButton={
            !isLoaded || draftFilterIsDirty ? handleApplyFilter : handleClose
          }
        />
      }
    >
      {organisationOptions.length > 1 && (
        <Card explanation="Changing the organisation will immediately reload the report and may affect other filters">
          <OptionField
            label={`Organisations (${showOutOf(
              organisationOptions.length,
              draftFilterValues.organisationIds.length,
            )})`}
            modalLabel="Organisations"
            labelAlignTop
            value={draftFilterValues.organisationIds}
            valueNumberOfLines={2}
            onChange={handleSetOrganisationIds}
            options={organisationOptions}
            noValueOption={{ name: 'All Organisations' }}
            showNoValueOption
            multiSelect
            showFieldTypeIcon={false}
            modalSize="small"
            modalSearchable
            modalEnableSelectAll
            showLabelInCloseHeader
            forceModal
            modalCloseOnDone={false}
            expandedInlineFirst
            expandedInlineLast
            showBadge={!!draftFilterValues.organisationIds.length}
          />
        </Card>
      )}
      <Card>
        <TextAreaField
          iconLeft={ICONS.MagnifyingGlassLight}
          value={draftFilterValues.search}
          name="search"
          onChangeWithName={handleSetFilterValue}
          placeholder="Search"
          showFieldTypeIcon={false}
          clearable
          onEsc={handleClose}
        />
      </Card>
      <Card>
        <OptionField
          label={`Checks (${showOutOf(
            checksOptions.length,
            draftFilterValues.checks.length,
          )})`}
          modalLabel="Checks"
          labelAlignTop
          value={draftFilterValues.checks}
          valueNumberOfLines={2}
          name="checks"
          showBadge={!!draftFilterValues.checks.length}
          onChangeWithName={handleSetFilterValue}
          options={checksOptions}
          noValueOption={{ name: 'All Checks' }}
          showNoValueOption
          multiSelect
          showFieldTypeIcon={false}
          modalSize="small"
          modalSearchable
          modalEnableSelectAll
          showLabelInCloseHeader
          expandedInlineFirst
        />
        <OptionField
          label={`Elements (${showOutOf(
            elementsOptions.length,
            draftFilterValues.elements.length,
          )})`}
          modalLabel="Elements"
          value={draftFilterValues.elements}
          name="elements"
          showBadge={!!draftFilterValues.elements.length}
          onChangeWithName={handleSetFilterValue}
          options={elementsOptions}
          noValueOption={{ name: 'All Elements' }}
          labelAlignTop
          valueNumberOfLines={2}
          showNoValueOption
          multiSelect
          showFieldTypeIcon={false}
          modalSize="small"
          modalSearchable
          modalEnableSelectAll
          showLabelInCloseHeader
        />
        <OptionField
          label={`Statuses (${showOutOf(
            filterOptions.statuses.length,
            draftFilterValues.statuses.length,
          )})`}
          modalLabel="Statuses"
          value={draftFilterValues.statuses}
          name="statuses"
          showBadge={!!draftFilterValues.statuses.length}
          onChangeWithName={handleSetFilterValue}
          options={filterOptions.statuses.map((s) => ({
            name: s.name,
            internalValue: s.value,
            icon: s.icon,
            iconColor: `#${s.color}`,
          }))}
          noValueOption={{ name: 'All Statuses' }}
          labelAlignTop
          valueNumberOfLines={2}
          multiSelect
          showFieldTypeIcon={false}
          modalSize="small"
          modalSearchable
          modalEnableSelectAll
          showLabelInCloseHeader
        />
        <OptionField
          label={`Current (${showOutOf(
            filterOptions.expiredStatuses.length,
            draftFilterValues.expiredStatuses.length,
          )})`}
          modalLabel="Current / Expired"
          value={draftFilterValues.expiredStatuses}
          showBadge={!!draftFilterValues.expiredStatuses.length}
          name="expiredStatuses"
          onChangeWithName={handleSetFilterValue}
          options={filterOptions.expiredStatuses.map((s) => ({
            name: s.name,
            internalValue: s.value,
          }))}
          noValueOption={{ name: 'Current, Expired' }}
          labelAlignTop
          multiSelect
          valueNumberOfLines={2}
          showFieldTypeIcon={false}
          modalSize="small"
          modalSearchable
          modalEnableSelectAll
          showLabelInCloseHeader
          expandedInlineLast
        />
      </Card>
      <Card label="Due Between">
        <DateRangeField
          placeholderFrom="From"
          placeholderTo="To"
          onChange={(value1, value2) => {
            handleSetFilterValues({
              dateFrom: value1 ? dateStringToNumber(value1) : null,
              dateTo: value2 ? dateStringToNumber(value2) : null,
            });
          }}
          fromValue={
            draftFilterValues.dateFrom
              ? numberToDateString(draftFilterValues.dateFrom)
              : ''
          }
          toValue={
            draftFilterValues.dateTo
              ? numberToDateString(draftFilterValues.dateTo)
              : ''
          }
        />
      </Card>
    </Page>
  );
};
